import {
  CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_BEGIN,
  CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_SUCCESS,
  CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_FAILURE,
  CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_DISMISS_FEEDBACK,
} from '../constants';
import { useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import api from 'common/api';
import { withCurrentCaseId } from 'common/selectors';
import { deleteItemImmutable } from 'utils/arrays';
import { fetchSearchContentHistory } from './fetchSearchContentHistory';

export const deleteSearchHistoryContentItem = createPromiseAction(
  CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_BEGIN,
);

export function dismissDeleteSearchHistoryContentItemFeedback() {
  return {
    type: CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_DISMISS_FEEDBACK,
  };
}

export function* doDeleteSearchHistoryContentItem(action) {
  const {
    payload: { caseId, searchHistoryItm },
  } = action;

  let res = yield call(api.del, `/cases/${caseId}/search-content-history/${searchHistoryItm.id}`);

  if (res && res.error) {
    yield put({
      type: CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_FAILURE,
      feedback: {
        message: 'feedback.deleteSearchHistoryItemFailure',
        error: res.error,
        retryAction: action,
      },
    });
    return yield call(rejectPromiseAction, action, res.error);
  }

  yield put({
    type: CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_SUCCESS,
    payload: { searchHistoryItm },
  });
  yield call(resolvePromiseAction, action, res);
  // Refresh list from server after successful delete
  yield put(fetchSearchContentHistory({}));
}

export function* watchDeleteSearchContentHistoryItem() {
  yield takeLatest(
    deleteSearchHistoryContentItem,
    withCurrentCaseId(doDeleteSearchHistoryContentItem),
  );
}

export function useDeleteSearchHistoryItem() {
  const dispatch = useDispatch();

  const { deleteSearchHistoryItemPending, deleteSearchHistoryItemFeedback } = useSelector(
    (state) => ({
      deleteSearchHistoryItemPending: state.case.deleteSearchHistoryItemPending,
      deleteSearchHistoryItemFeedback: state.case.deleteSearchHistoryItemFeedback,
    }),
    shallowEqual,
  );

  const boundAction = useCallback(
    (...args) => {
      return dispatch(deleteSearchHistoryContentItem(...args));
    },
    [dispatch],
  );

  const boundDismissFeedback = useCallback(() => {
    return dispatch(dismissDeleteSearchHistoryContentItemFeedback());
  }, [dispatch]);

  return {
    deleteSearchHistoryItem: boundAction,
    deleteSearchHistoryItemPending,
    deleteSearchHistoryItemFeedback,
    dismissDeleteSearchHistoryItemFeedback: boundDismissFeedback,
  };
}

export function reducer(state, action) {
  const getIndex = (itemId) =>
    state.searchHistory && state.searchHistory.findIndex(({ id }) => id === itemId);

  switch (action.type) {
    case CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_BEGIN + '.TRIGGER':
      // Just after a request is sent
      return {
        ...state,
        deleteSearchHistoryItemPending: true,
        deleteSearchHistoryItemFeedback: null,
      };

    case CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_SUCCESS:
      // The request is success
      return {
        ...state,
        deleteSearchHistoryItemPending: false,
        deleteSearchHistoryItemFeedback: action.feedback,
      };

    case CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_FAILURE:
      // The request is failed
      return {
        ...state,
        deleteSearchHistoryItemPending: false,
        deleteSearchHistoryItemFeedback: action.feedback,
      };

    case CASE_DELETE_SEARCH_CONTENT_HISTORY_ITEM_DISMISS_FEEDBACK:
      // Dismiss the request failure error
      return {
        ...state,
        deleteSearchHistoryItemFeedback: null,
      };

    default:
      return state;
  }
}

// Backward-compat exports to avoid breaking existing imports during migration
export {
  watchDeleteSearchContentHistoryItem as watchDeleteSearchHistoryItem,
  doDeleteSearchHistoryContentItem as doDeleteSearchHistoryItem,
  deleteSearchHistoryContentItem as deleteSearchHistoryItem,
  dismissDeleteSearchHistoryContentItemFeedback as dismissDeleteSearchHistoryItemFeedback,
};
